/**
 * A GPX route for the terrain generator: read in the browser, given an area to generate, and
 * placed in the generated area's local frame.
 *
 * Recorded tracks (`trkpt`, what watches and Strava export) and planned routes (`rtept`, what
 * route planners export) are both read; segments join in file order. The file never leaves
 * the browser.
 */

import type { CoursePoint, CourseSample } from '@/lib/virtual-course/course'
import {
  boundsOf,
  centroidOf,
  createProjection,
  padBounds,
  type LngLatBounds,
  type LocalBounds,
  type Projection,
} from '@/lib/virtual-course/projection'
import { MAX_SIDE_KM, boxSize } from './grid'

/** Larger files are not routes a person ran or rode. */
export const MAX_GPX_BYTES = 25 * 1024 * 1024

export interface GpxRoute {
  /** The track or route name, else the file name. */
  name: string | null
  points: CoursePoint[]
  /** Length along the points, in metres. */
  lengthM: number
  bounds: LngLatBounds
}

export type GpxProblem = 'not-gpx' | 'no-points' | 'too-big'

export class GpxError extends Error {
  readonly problem: GpxProblem
  constructor(problem: GpxProblem) {
    super(problem)
    this.name = 'GpxError'
    this.problem = problem
  }
}

/** Elements by local name, whatever namespace prefix the exporting app used. */
function byName(parent: Document | Element, name: string): Element[] {
  return Array.from(parent.getElementsByTagNameNS('*', name))
}

function childText(parent: Element, name: string): string | null {
  const child = Array.from(parent.children).find((node) => node.localName === name)
  const text = child?.textContent?.trim()
  return text ? text : null
}

function toPoint(node: Element): CoursePoint | null {
  const latitude = Number(node.getAttribute('lat'))
  const longitude = Number(node.getAttribute('lon'))
  if (!node.hasAttribute('lat') || !node.hasAttribute('lon')) return null
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null
  const ele = childText(node, 'ele')
  const elevation = ele === null ? NaN : Number(ele)
  return Number.isFinite(elevation) ? { latitude, longitude, elevation } : { latitude, longitude }
}

/** Length of a short route in metres, flat-earth around its centroid (well under 0.1 % off at 50 km). */
function routeLength(points: CoursePoint[]): number {
  const projection = createProjection(centroidOf(points))
  let length = 0
  let previous = projection.toLocal(points[0])
  for (let i = 1; i < points.length; i++) {
    const next = projection.toLocal(points[i])
    length += Math.hypot(next.x - previous.x, next.y - previous.y)
    previous = next
  }
  return length
}

/** Parse GPX text. Throws `GpxError` when there is nothing to draw. */
export function parseGpx(text: string, fileName?: string): GpxRoute {
  const xml = new DOMParser().parseFromString(text, 'application/xml')
  if (byName(xml, 'parsererror').length || xml.documentElement?.localName !== 'gpx') {
    throw new GpxError('not-gpx')
  }
  // A recording wins over a plan when a file carries both.
  const trackPoints = byName(xml, 'trkpt')
  const nodes = trackPoints.length ? trackPoints : byName(xml, 'rtept')
  const points = nodes.map(toPoint).filter((point): point is CoursePoint => point !== null)
  if (points.length < 2) throw new GpxError('no-points')

  const owner = byName(xml, trackPoints.length ? 'trk' : 'rte')[0]
  const metadata = byName(xml, 'metadata')[0]
  const name =
    (owner && childText(owner, 'name')) ??
    (metadata && childText(metadata, 'name')) ??
    (fileName ? fileName.replace(/\.gpx$/i, '') : null)

  return { name, points, lengthM: routeLength(points), bounds: boundsOf(points) }
}

/** Read a picked or dropped file. Throws `GpxError` for files that are not a usable GPX. */
export async function readGpxFile(file: File): Promise<GpxRoute> {
  if (file.size > MAX_GPX_BYTES) throw new GpxError('too-big')
  return parseGpx(await file.text(), file.name)
}

/**
 * The area to generate around a route: its bounds plus a margin of surrounding country
 * (15 % of the longer side, 600 m to 4 km), trimmed so the area stays within the size limit
 * when the route itself fits.
 */
export function routeArea(route: Pick<GpxRoute, 'bounds'>): LngLatBounds {
  const { widthM, heightM } = boxSize(route.bounds)
  const longest = Math.max(widthM, heightM)
  const room = (MAX_SIDE_KM * 1000 * 0.98 - longest) / 2
  const margin = Math.min(Math.max(600, longest * 0.15), 4000)
  return padBounds(route.bounds, room > 0 ? Math.min(margin, room) : margin)
}

/** The box around every one of `boxes`. */
export function unionBounds(boxes: readonly LngLatBounds[]): LngLatBounds {
  if (!boxes.length) throw new Error('unionBounds: no boxes')
  return boxes.reduce((union, box) => ({
    north: Math.max(union.north, box.north),
    south: Math.min(union.south, box.south),
    east: Math.max(union.east, box.east),
    west: Math.min(union.west, box.west),
  }))
}

/**
 * An event's world: the area around all of its courses (their bounds together, with `routeArea`'s margin), widened on
 * its shorter side to a square about the same centre. A square world sits as a whole block in any view, where a long
 * thin course would leave a strip of land. The longer side is unchanged, so the grids keep their cell size and the size
 * limit applies as before; a narrow course costs more cells and triangles, at most the square of the longer side.
 */
export function worldArea(courses: readonly LngLatBounds[]): LngLatBounds {
  const area = routeArea({ bounds: unionBounds(courses) })
  const { widthM, heightM } = boxSize(area)
  const side = Math.max(widthM, heightM)
  const centre = { latitude: (area.north + area.south) / 2, longitude: (area.east + area.west) / 2 }
  const halfLat = ((area.north - area.south) / 2) * (side / heightM)
  const halfLng = ((area.east - area.west) / 2) * (side / widthM)
  return {
    north: centre.latitude + halfLat,
    south: centre.latitude - halfLat,
    east: centre.longitude + halfLng,
    west: centre.longitude - halfLng,
  }
}

/**
 * The generated area's local frame: metres east and north of its centre, linear in longitude
 * and latitude, exactly as the low-poly mesh lays out its grid.
 */
export function areaProjection(bounds: LngLatBounds, widthM: number, heightM: number): Projection {
  const origin = { longitude: (bounds.west + bounds.east) / 2, latitude: (bounds.south + bounds.north) / 2 }
  const metresPerDegreeLng = widthM / (bounds.east - bounds.west)
  const metresPerDegreeLat = heightM / (bounds.north - bounds.south)
  return {
    origin,
    metresPerDegreeLng,
    metresPerDegreeLat,
    toLocal: (point) => ({
      x: (point.longitude - origin.longitude) * metresPerDegreeLng,
      y: (point.latitude - origin.latitude) * metresPerDegreeLat,
    }),
    toLngLat: (point) => ({
      longitude: origin.longitude + point.x / metresPerDegreeLng,
      latitude: origin.latitude + point.y / metresPerDegreeLat,
    }),
  }
}

/**
 * The stretches of a route that lie inside the area, in order. A route that leaves the area
 * and comes back becomes two stretches, so nothing is drawn across the gap.
 */
export function stretchesInside<T extends { x: number; y: number }>(samples: T[], bounds: LocalBounds): T[][] {
  const stretches: T[][] = []
  let current: T[] = []
  for (const sample of samples) {
    const inside =
      sample.x >= bounds.minX && sample.x <= bounds.maxX && sample.y >= bounds.minY && sample.y <= bounds.maxY
    if (inside) current.push(sample)
    else if (current.length) {
      stretches.push(current)
      current = []
    }
  }
  if (current.length) stretches.push(current)
  return stretches.filter((stretch) => stretch.length >= 2)
}

/** Share of a route's length that lies inside the area, 0–1. */
export function shareInside(samples: CourseSample[], bounds: LocalBounds): number {
  if (samples.length < 2) return 0
  const total = samples[samples.length - 1].distance - samples[0].distance
  if (total <= 0) return 1
  const inside = stretchesInside(samples, bounds).reduce(
    (sum, stretch) => sum + stretch[stretch.length - 1].distance - stretch[0].distance,
    0,
  )
  return Math.min(1, inside / total)
}
